import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useIsMobileOrTabletDevice } from '../_custom/Hooks/useIsMobileOrTabletDevice';
import { useCloseOnBack } from '../_custom/Hooks/useCloseOnBack';
import DocumentViewer from './DocumentViewer';
import DocxDrawer from './DocxDrawer';
import { SheetEditorDrawer } from '../SheetEditor';
import { SPREADSHEET_TYPES, DOCX_TYPES } from './constants';
import { useAddToReportsVisible } from '../Reports/useAddToReportsVisible';
import {
    classifyAddable,
    addFileUrlToReports,
    addOriginalXlsxToReports,
    addEditedFileToReports,
    ADD_TO_REPORTS_FAILED_MESSAGE,
} from '../Reports/addFileToReports';
import { isEditedWorkspaceSnapshot } from '../Reports/editedWorkspaceSnapshot';
import { startAddToWorkspaceTracking, trackUnsavedEditsPrompt } from '../Reports/workspaceAnalytics';
import type { WorkspaceFileKind, WorkspaceSource } from '../Reports/workspaceAnalytics';
import type { UnsavedEditsPromptAction } from './useUnsavedEditsGuard';
import { isReadOnlyAddBand } from '../SheetEditor';
import { showAddedToReportsToast } from '../Reports/addedToReportsToast';
import WhiteCheckIcon from '../Reports/WhiteCheckIcon';
import { sheetLoadErrorMessage } from '../SheetEditor/sheetSizeGuard';
import { useAppSelector } from '../../app/hooks';
import { downloadIconDataRoomHovered } from '../../assets/hostedassets';
import styles from './FileViewerDrawer.module.scss';

interface FileViewerDrawerProps {
    heading: string;
    fileUrl: string;
    mimeType?: string;
    /** Original file name — drives Add-to-Reports classification and the bottom
     *  drawer's title/download name. Defaults to `heading` (callers that only
     *  have a display heading, not a real file name, still classify fine via
     *  `mimeType`). */
    fileName?: string;
    downloading?: boolean;
    downloadLabel?: string;
    onDownload: () => void;
    onClose: () => void;
    /** Overrides the org id used for Add-to-Reports (default: resolved from the
     *  route's `:id` param). Callers outside a per-org route — e.g. the global
     *  Magic Upload drawer — pass the file's own tagged org here instead, since
     *  the route param may be absent or belong to a different org. */
    assesseeOrgId?: string;
    /** Optional extra content rendered left of the Download button in the drawer header. */
    renderHeaderExtra?: React.ReactNode;
    /** Off for a file already in Workspace, where adding again would duplicate the row. */
    allowAddToReports?: boolean;
    /** Spreadsheet/docx open as a read-only preview. */
    viewOnly?: boolean;
    /** Which surface this viewer belongs to, for Add to Workspace analytics. */
    workspaceSource?: WorkspaceSource;
}

/**
 * Shared preview drawer used by the ~8 dashboard preview sites, the dataroom
 * (File) and the GST Analysis report. Dispatches every mime type to the same
 * BottomDrawer surface the rest of the app uses — spreadsheets/docx to the
 * editable SheetEditorDrawer/DocxDrawer, everything else (pdf/image/unknown)
 * to a BottomDrawer hosting the read-only DocumentViewer. No side drawer
 * remains anywhere in this component. Add-to-Reports on every bottom drawer
 * (the read-only one too: a cited PDF, RECUR-27849 gap G) is
 * gated by `useAddToReportsVisible` plus an org id — the `assesseeOrgId` prop
 * when the caller passes one (e.g. Magic Upload, keyed off the file's own
 * tagged org), else resolved from the route (`useParams().id` — the same param
 * the Dashboard pages are keyed on); when no org id is resolvable (e.g. Data
 * Room's pdf/image-only usage), the gate stays off rather than crashing.
 */
const FileViewerDrawer: React.FC<FileViewerDrawerProps> = ({
    heading,
    fileUrl,
    mimeType,
    fileName,
    downloading = false,
    downloadLabel = 'Download',
    onDownload,
    onClose,
    assesseeOrgId: assesseeOrgIdOverride,
    renderHeaderExtra,
    allowAddToReports = true,
    viewOnly = false,
    workspaceSource = 'FILE_VIEWER',
}) => {
    const resolvedFileName = fileName ?? heading;
    const isMobileOrTablet = useIsMobileOrTabletDevice();
    const isPlainViewer = !(mimeType && (SPREADSHEET_TYPES.includes(mimeType) || DOCX_TYPES.includes(mimeType)));
    useCloseOnBack(isPlainViewer && isMobileOrTablet, onClose);
    const params = useParams();
    const assesseeOrgId = assesseeOrgIdOverride ?? params.id;
    const user = useAppSelector((state) => state.user.userData);
    const addToReportsVisible = useAddToReportsVisible();
    const [addingToReports, setAddingToReports] = useState(false);
    const [addedToReports, setAddedToReports] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );
    // Synchronous: the button only disables after the re-render, so a double click could start two adds.
    const addingToReportsRef = useRef(false);

    const canAddToReports =
        allowAddToReports && addToReportsVisible && Boolean(assesseeOrgId) && classifyAddable(resolvedFileName, mimeType) !== null;

    // `payload` arrives from the Sheet/Docx drawer: a read-only-band workbook's
    // bytes, or the on-screen content when the user changed the file — that
    // snapshot lands in Workspace; an untouched file is fetched and
    // snapshotted from its URL exactly as before.
    const handleAddToReports = canAddToReports
        ? async (payload?: unknown) => {
              if (addingToReportsRef.current) return;
              addingToReportsRef.current = true;
              setAddingToReports(true);
              // A fresh add must not read as succeeded off a previous add's lingering
              // "Added" state — the drawers key their close-after-add on this flag.
              setAddedToReports(false);
              const tracker = startAddToWorkspaceTracking(
                  { source: workspaceSource, fileName: resolvedFileName, fileType: mimeType, organizationId: assesseeOrgId, user },
                  payload,
              );
              try {
                  const reportArgs = {
                      fileName: resolvedFileName,
                      assesseeOrgId: assesseeOrgId as string,
                      investorOrgId: user?.investorOrgId,
                      createdBy: user?.primaryEmail,
                  };
                  // A read-only-band workbook hands over the bytes the drawer already holds.
                  if (isReadOnlyAddBand(payload)) await addOriginalXlsxToReports({ ...payload, ...reportArgs });
                  else if (isEditedWorkspaceSnapshot(payload)) await addEditedFileToReports({ ...reportArgs, snapshot: payload });
                  else await addFileUrlToReports({ ...reportArgs, fileUrl, fileType: mimeType });
                  tracker.succeeded();
                  showAddedToReportsToast(
                      () => window.open(`/investor/app/reports/${assesseeOrgId}`, '_blank', 'noopener,noreferrer'),
                      () => {
                          if (isMountedRef.current) setAddedToReports(false);
                      },
                  );
                  setAddedToReports(true);
              } catch (err) {
                  console.error('Failed to add file to Reports:', err instanceof Error ? err.message : 'unknown error');
                  toast.error(sheetLoadErrorMessage(err, ADD_TO_REPORTS_FAILED_MESSAGE));
              } finally {
                  tracker.failed(); // no-op after succeeded()
                  addingToReportsRef.current = false;
                  setAddingToReports(false);
              }
          }
        : undefined;

    const trackPrompt =
        (fileKind: WorkspaceFileKind) =>
        (action: UnsavedEditsPromptAction): void =>
            trackUnsavedEditsPrompt(action, { source: workspaceSource, fileKind, organizationId: assesseeOrgId, user });

    if (mimeType && SPREADSHEET_TYPES.includes(mimeType)) {
        return (
            <SheetEditorDrawer
                open
                onClose={onClose}
                fileName={resolvedFileName}
                fileUrl={fileUrl}
                mimeType={mimeType}
                onAddToReports={handleAddToReports}
                addingToReports={addingToReports}
                addedToReports={addedToReports}
                onUnsavedEditsPrompt={trackPrompt('SPREADSHEET')}
                viewOnly={viewOnly}
            />
        );
    }

    if (mimeType && DOCX_TYPES.includes(mimeType)) {
        return (
            <DocxDrawer
                open
                onClose={onClose}
                fileName={resolvedFileName}
                fileUrl={fileUrl}
                onDownload={onDownload}
                downloading={downloading}
                // Passed straight through: the edited document is the drawer's
                // argument (handleAddToReports narrows it at runtime, so a stray
                // click event still takes the original-file path).
                onAddToReports={handleAddToReports}
                addingToReports={addingToReports}
                addedToReports={addedToReports}
                onUnsavedEditsPrompt={trackPrompt('DOCUMENT')}
                viewOnly={viewOnly}
            />
        );
    }

    return (
        <BottomDrawer
            open
            onClose={onClose}
            title={heading}
            headerRight={
                <div className={styles.headerActions}>
                    {renderHeaderExtra}
                    <Button
                        type="button"
                        aria-label={`Download ${heading}`}
                        text={downloadLabel}
                        className={styles.downloadBtn}
                        icon={
                            downloading ? (
                                <LoadingSpinner color="#6021B3" style={{ margin: '0', padding: '0' }} />
                            ) : (
                                downloadIconDataRoomHovered
                            )
                        }
                        iconStyles={{ height: '0.9375rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload();
                        }}
                    />
                    {handleAddToReports && (
                        <Button
                            type="button"
                            text={addedToReports ? <WhiteCheckIcon /> : 'Add to Workspace'}
                            variant="purple"
                            className={styles.headerBtn}
                            onClick={addedToReports ? () => {} : () => handleAddToReports()}
                            disabled={addingToReports}
                            loading={!addedToReports && addingToReports}
                        />
                    )}
                </div>
            }
            className={styles.previewDrawer}
            bodyClassName={styles.previewBody}
            height="100vh"
        >
            <DocumentViewer fileUrl={fileUrl} mimeType={mimeType} />
        </BottomDrawer>
    );
};

export default FileViewerDrawer;
